import * as React from "react";
import {
  DataGrid as MUIDataGrid,
  GridColDef,
  GridColumnResizeParams,
  GridRowModel,
  GridRowId,
  GridRowModes,
  GridRowModesModel,
  GridRowsProp,
  useGridApiRef,
} from "@mui/x-data-grid";

import { AddButtonIcon, TrashIcon } from "@/components/icons";
import {
  buildEmptyRow,
  canAddRow,
  getDefaultCellValue,
  getHeaderField,
  getRequiredError,
  isEditModeHeader,
  isUserEditableHeader,
  parseRowsFromValue,
} from "./grid-data-grid.helpers";
import { createGridColumns } from "./grid-data-grid.columns";
import { GridHeaderItem } from "./grid-data-grid.types";
import { gridSx } from "./grid-data-grid.sx";
import { registerFormCommit } from "@/utils/form-commit-registry";

export type { GridHeaderItem } from "./grid-data-grid.types";

type Props = {
  value: unknown;
  onChange?: (nextValue: string) => void;
  headers: GridHeaderItem[];
  maxRows?: number;
  readonly?: boolean;
  disabled?: boolean;
  showHiddenColumnsPreview?: boolean;
};

export function DataGrid({
  value,
  onChange,
  headers,
  maxRows,
  readonly = false,
  disabled = false,
  showHiddenColumnsPreview = false,
}: Props) {
  const apiRef = useGridApiRef();
  const headerSignature = React.useMemo(
    () =>
      headers
        .map(
          (h) =>
            `${h.key}:${h.keyValue}:${h.type}:${h.label}:${Boolean(h.hide)}:${Boolean(h.required)}:${Boolean(h.readonly)}:${Boolean(h.disabled)}:${Boolean(h.searchInOptions)}`,
        )
        .join("|"),
    [headers],
  );
  const stableHeaders = React.useMemo(() => headers, [headerSignature]);
  const deletingRowIdsRef = React.useRef<Set<string>>(new Set());
  const [rows, setRows] = React.useState<GridRowsProp>(() =>
    parseRowsFromValue(value),
  );
  const [columnWidths, setColumnWidths] = React.useState<
    Record<string, number>
  >({});
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {},
  );
  const rowModesModelRef = React.useRef(rowModesModel);
  React.useEffect(() => {
    rowModesModelRef.current = rowModesModel;
  });

  const getEditingRowIds = React.useCallback(() => {
    const editingIds = new Set<GridRowId>(
      Object.entries(rowModesModelRef.current)
        .filter(([, m]) => m?.mode === GridRowModes.Edit)
        .map(([id]) => id),
    );

    rows.forEach((row) => {
      try {
        if (apiRef.current?.getRowMode(row.id) === GridRowModes.Edit) {
          editingIds.add(row.id);
        }
      } catch {
        // row mode may be unavailable while the grid is initializing
      }
    });

    return [...editingIds];
  }, [apiRef, rows]);

  React.useEffect(() => {
    return registerFormCommit(async () => {
      const editingIds = getEditingRowIds();
      for (const id of editingIds) {
        try {
          const row = apiRef.current?.getRow(id);
          if (!row) continue;
          apiRef.current?.stopRowEditMode({ id });
        } catch {
          // row may already have exited edit mode
        }
      }
      // One tick so processRowUpdate → emitRows → onChange can flush.
      await new Promise<void>((resolve) => setTimeout(resolve, 0));

      const remainingEditingIds = editingIds.filter(
        (id) => rowModesModelRef.current[id]?.mode === GridRowModes.Edit,
      );
      if (remainingEditingIds.length > 0) {
        throw new Error("Please fill out all required grid values.");
      }
    });
  }, [apiRef, getEditingRowIds]);

  const isGridDisabled = disabled || typeof onChange !== "function";
  const canManageRows = !readonly && !isGridDisabled;
  const canAdd = canManageRows && canAddRow(rows.length, maxRows);

  const sanitizeRowModesModel = React.useCallback(
    (model: GridRowModesModel, sourceRows: GridRowsProp): GridRowModesModel => {
      const existingIds = new Set(sourceRows.map((row) => String(row.id)));
      const next = Object.entries(model).reduce<GridRowModesModel>(
        (acc, [id, value]) => {
          if (existingIds.has(id)) {
            acc[id] = value;
          }
          return acc;
        },
        {},
      );

      return next;
    },
    [],
  );

  const serializeRowsForValue = React.useCallback(
    (sourceRows: GridRowsProp) => {
      const allowedFields = new Set(["id", "isNew"]);
      stableHeaders
        .filter((header) => header.hide !== true)
        .forEach((header) => {
          allowedFields.add(getHeaderField(header));
        });

      return sourceRows.map((row) => {
        const rowRecord = row as Record<string, unknown>;
        return Object.fromEntries(
          Object.entries(rowRecord).filter(([field]) =>
            allowedFields.has(field),
          ),
        );
      });
    },
    [stableHeaders],
  );

  const emitRows = React.useCallback(
    (nextRows: GridRowsProp) => {
      setRows(nextRows);

      if (typeof onChange === "function") {
        onChange(JSON.stringify(serializeRowsForValue(nextRows)));
      }
    },
    [onChange, serializeRowsForValue],
  );

  const columns = React.useMemo(() => {
    const gridHeaders = showHiddenColumnsPreview
      ? stableHeaders
      : stableHeaders.filter((header) => header.hide !== true);

    const baseColumns = createGridColumns(
      gridHeaders,
      isGridDisabled,
      showHiddenColumnsPreview,
    ).map((column) => {
      const resizedWidth = columnWidths[column.field];
      if (!resizedWidth) return column;
      return { ...column, width: resizedWidth };
    });

    if (!canManageRows) {
      return baseColumns;
    }

    const actionsColumn: GridColDef = {
      field: "__actions__",
      headerName: "Action",
      flex: 1,
      minWidth: 120,
      sortable: false,
      filterable: false,
      hideable: false,
      disableColumnMenu: true,
      editable: false,
      resizable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <button
          type="button"
          onClick={() => {
            const rowId = String(params.id);
            deletingRowIdsRef.current.add(rowId);

            try {
              params.api.stopRowEditMode({
                id: params.id,
                ignoreModifications: true,
              });
            } catch {
              // no-op: row might not be in edit mode
            }

            setRowModesModel((prev) => {
              if (!(params.id in prev)) {
                return prev;
              }
              const next = { ...prev };
              delete next[params.id];
              return next;
            });

            setTimeout(() => {
              setRows((prevRows) => {
                const nextRows = prevRows.filter(
                  (row) => String(row.id) !== rowId,
                );
                if (typeof onChange === "function") {
                  onChange(JSON.stringify(serializeRowsForValue(nextRows)));
                }
                return nextRows;
              });
              deletingRowIdsRef.current.delete(rowId);
            }, 0);
          }}
          className="inline-flex items-center justify-center text-secondary-text hover:text-red"
          aria-label="Delete row"
          title="Delete row"
        >
          <TrashIcon className="h-4 w-4 text-red" />
        </button>
      ),
    };

    return [...baseColumns, actionsColumn];
  }, [
    columnWidths,
    emitRows,
    canManageRows,
    isGridDisabled,
    onChange,
    showHiddenColumnsPreview,
    stableHeaders,
  ]);

  const handleColumnWidthChange = React.useCallback(
    (params: GridColumnResizeParams) => {
      const field = String(params.colDef.field);
      setColumnWidths((prev) => {
        if (prev[field] === params.width) return prev;
        return { ...prev, [field]: params.width };
      });
    },
    [],
  );

  React.useEffect(() => {
    const nextRows = parseRowsFromValue(value);
    setRows(nextRows);
  }, [value]);

  React.useEffect(() => {
    if (!rows.length || !stableHeaders.length) return;

    const nextRows = rows.map((row) => {
      const nextRow = { ...row } as Record<string, unknown>;
      let changed = false;

      stableHeaders.forEach((header) => {
        const primaryField = getHeaderField(header);
        const legacyField = header.key?.trim();

        if (!(primaryField in nextRow)) {
          if (
            legacyField &&
            legacyField !== primaryField &&
            legacyField in nextRow
          ) {
            nextRow[primaryField] = nextRow[legacyField];
          } else {
            nextRow[primaryField] = getDefaultCellValue(header);
          }
          changed = true;
        }

        if (
          legacyField &&
          legacyField !== primaryField &&
          legacyField in nextRow
        ) {
          delete nextRow[legacyField];
          changed = true;
        }

        if (
          header.keyValue &&
          header.keyValue !== primaryField &&
          header.keyValue in nextRow
        ) {
          delete nextRow[header.keyValue];
          changed = true;
        }
      });

      return changed ? (nextRow as GridRowModel) : row;
    });

    if (nextRows.every((row, index) => row === rows[index])) return;

    emitRows(nextRows);
  }, [emitRows, rows, stableHeaders]);

  const processRowUpdate = (newRow: GridRowModel): GridRowModel => {
    if (deletingRowIdsRef.current.has(String(newRow.id))) {
      return newRow;
    }

    const validationField = stableHeaders[0]
      ? getHeaderField(stableHeaders[0])
      : "";
    let latestRow = newRow;

    try {
      latestRow = apiRef.current?.getRowWithUpdatedValues(
        newRow.id,
        validationField,
      ) as GridRowModel;
    } catch {
      latestRow = newRow;
    }

    const updatedRow = { ...latestRow, isNew: false };
    const updatedRowRecord = updatedRow as Record<string, unknown>;
    let firstErrorField: string | undefined;
    let firstErrorMessage: string | undefined;

    stableHeaders.forEach((header) => {
      const fieldKey = getHeaderField(header);
      const error = getRequiredError(header, updatedRowRecord[fieldKey]);
      if (error && !firstErrorField) {
        firstErrorField = fieldKey;
        firstErrorMessage = error;
      }
    });

    if (firstErrorField) {
      setRowModesModel((oldModel) => ({
        ...oldModel,
        [newRow.id]: { mode: GridRowModes.Edit, fieldToFocus: firstErrorField },
      }));
      throw new Error(firstErrorMessage ?? "Row has validation errors");
    }

    const newRows = rows.map((row) =>
      row.id === newRow.id ? updatedRow : row,
    );
    emitRows(newRows);
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(sanitizeRowModesModel(newRowModesModel, rows));
  };

  const handleAddRow = () => {
    if (!canManageRows || !canAdd) return;

    const newRow = buildEmptyRow(stableHeaders);
    const newRows = [...rows, newRow];
    const focusHeader =
      stableHeaders.find(isUserEditableHeader) ??
      stableHeaders.find(isEditModeHeader);
    const firstField = focusHeader ? getHeaderField(focusHeader) : undefined;

    emitRows(newRows);

    if (!focusHeader) {
      return;
    }

    setRowModesModel((oldModel) => ({
      ...oldModel,
      [newRow.id]: firstField
        ? { mode: GridRowModes.Edit, fieldToFocus: firstField }
        : { mode: GridRowModes.Edit },
    }));
  };

  return (
    <div className="h-fit">
      <MUIDataGrid
        apiRef={apiRef}
        rows={rows}
        columns={columns.length ? columns : []}
        editMode="row"
        sx={gridSx}
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        processRowUpdate={processRowUpdate}
        onColumnWidthChange={handleColumnWidthChange}
        // onProcessRowUpdateError={(error) => {
        //   setGridError(error instanceof Error ? error.message : String(error));
        // }}
        hideFooter
        disableColumnSelector
        disableColumnResize={false}
        rowSpacingType="border"
        rowSelection={false}
      />
      {/* {!!gridError && <ValidationError>{gridError}</ValidationError>} */}
      {canManageRows && (
        <button
          type="button"
          onClick={handleAddRow}
          disabled={!canAdd}
          className="flex flex-row items-center gap-2 font-medium text-giant-blue disabled:text-secondary-text disabled:cursor-not-allowed pt-2.5"
        >
          <AddButtonIcon />
          add a row
        </button>
      )}
    </div>
  );
}
